import { VaspDueDiligenceReviewStatus } from "~/templates/companies/types";
import { JurisdictionStatus, VASP } from "~/types/vaspid";

export interface tfListVASPsWithTrustInfo_vasps_data {
  did: string;
  hasAdmin: boolean | null;
  isActiveSender: boolean | null;
  isActiveReceiver: boolean | null;
  isNotifiable?: boolean;
  documents:
    | {
        mimeType: string;
        name: string;
      }[]
    | string
    | null;
  fields: {
    value: string;
    issuer: string | null;
    issuanceDate: string;
  }[];
  trustVASP: {
    trusted: boolean;
  };
}

export interface tfListVASPsWithTrustInfo_vasps_pagination {
  page: number;
  per_page: number;
  order: string | null;
  total: number;
}

export interface ListVASPInput {
  q?: string | null;
  page?: number | null;
  per_page?: number | null;
  order?: string | null;
}

export interface ListVASPResponse {
  vasps: tfListVASPsWithTrustInfo_vasps_data[];
  pagination: tfListVASPsWithTrustInfo_vasps_pagination;
}

export type BadgeType = "super_vasp" | "verified" | "in_network" | "claimed";
export type ListingType = "exclude_subsidiaries" | "all" | "exclude_gateways";

export interface SimpleListVASPInput {
  q?: string | null;
  page?: number | null;
  per_page?: number | null;
  order?: string | null;
  fields?: string[] | null;
  all?: boolean | null;
  websiteDomain?: string | null;
  hasAdmin?: boolean;
  badge?: BadgeType;
  jurisdictions?: string;
  listingType?: ListingType;
  includeUncheckedVASPs?: boolean;
  includeActiveSendersOnly?: string;
  includeActiveReceiversOnly?: string;
  regulatoryStatus?: string;
  reviewValue?: VaspDueDiligenceReviewStatus | "NULL";
  reviewedByVaspDID?: string;
  filterByJurisdictionStatus?: JurisdictionStatus;
}

export interface UserVASPListInput {
  fields?: string[] | null;
}

export type UserVASPListResponse = {
  claimable: VASP[];
  joinable: VASP[];
  loginable: VASP[];
};

// eslint-disable-next-line no-shadow
export enum StatusToProcessBlockchainOptions {
  CREATED = "CREATED",
  ACCEPTED = "ACCEPTED",
  ACK = "ACK",
  SENT = "SENT",
}

export type VaspSettingsPayload = {
  autoconfirm?: boolean;
  statusToProcessBlockchain?: null | StatusToProcessBlockchainOptions;
  vaspDiscoverability?: boolean;
  requestTrackingForMissingData?: boolean;
  autoDecryption?: boolean;
};

export type SaveVaspSettingsInput = {
  vaspDID: string;
  autoconfirm?: boolean;
  statusToProcessBlockchain?: null | StatusToProcessBlockchainOptions;
  vaspDiscoverability?: boolean;
  autoDecryption?: boolean;
};

export type VaspnetSearchResponseItem = {
  id: string;
  vaspIdentifier: string;
  vaspName?: string | null;
  vaspOtherName?: string | null;
  supervisoryAuthority?: string | null;
  countryCode?: string | null;
  licenseRegistrationId?: string | null;
  regulatoryStatus?: string | null;
  regulatoryStatusDate?: Date | null;
  statusStartDate?: Date | null;
  statusExpiryDate?: Date | null;
  lastChecked?: Date | null;
  regulatoryData?: any | null;
};

export type VaspTopCounterparty = {
  vaspDID: string;
  vaspName: string;
  vaspLogoURL?: string | null;
  jurisdiction?: string | null;
  totalIncomingTransactions: number;
  totalOutgoingTransactions: number;
  travelRuleIncomingTransactions: number;
  travelRuleOutgoingTransactions: number;
  sendsTravelRuleData: boolean;
  repliesTravelRuleData: boolean;
  isVerified: boolean;
  isClaimed: boolean;
  isInNetwork: boolean;
  reviewValue: VaspDueDiligenceReviewStatus | null;
  reviewNote: string | null;
  reviewDate: string | null;
  reviewedBy: string | null;
};
export type VaspTopCounterPartiesResponse = {
  lastUpdated: Date;
  results: VaspTopCounterparty[];
};

export type VaspnetSearchResponse = {
  vaspResults: VaspnetSearchResponseItem[];
};

export interface UploadUrlResponse {
  uploadUrl: string;
}

// eslint-disable-next-line no-shadow
export enum DocumentType {
  questionnaire = "DDQ for Travel Rule Data Sharing",
}

// eslint-disable-next-line no-shadow
export enum DocumentAccessAction {
  REQUEST = "REQUEST",
  APPROVE = "APPROVE",
  DECLINE = "DECLINE",
  REVOKE = "REVOKE",
}

// eslint-disable-next-line no-shadow
export enum DocumentRequestStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  DECLINED = "DECLINED",
  REVOKED = "REVOKED",
  WAITING_FOR_INFORMATION = "WAITING_FOR_INFORMATION",
}

export interface DocumentRequest {
  documentType: DocumentType;
  status: DocumentRequestStatus;
  createdAt: string;
  ownerVASPdid: string;
  requestingVASPdid: string;
  metadata?: string;
}

export interface DocumentRequestInput extends URLSearchParams {
  vaspDID: string;
  requestingVASPdid: string;
  accessAction: DocumentAccessAction;
  documentType: DocumentType;
  metadata?: string;
}

export interface TfUploadVASPDocumentResponse {
  uploadUrl: string;
}

export interface VASPUpdate {
  did?: string | null;
  fields: {
    fieldName: string;
    values: {
      value: string;
    }[];
  }[];
}

export type DDQDocumentData = { [key: string]: string | string[] };
export type DDQDocument = { updatedDate: string; data: DDQDocumentData };

export type VASPDueDiligengeReviewPayload = {
  reviewedVaspDID: string;
  reviewValue: VaspDueDiligenceReviewStatus;
  includeInWidget?: string | null;
  note?: string;
};

export type VASPDueDiligengeReviewResponse = {
  vaspDID: string;
  reviewedVaspDID: string;
  reviewValue: VaspDueDiligenceReviewStatus;
  note: string | null;
  createdBy: string;
  createdAt: Date;
};

export type PatchEntityEnablementParams = {
  did: string;
  action: "activate" | "deactivate";
};

export type PatchManageSubsidiariesParams = {
  groupVaspDid: string;
  subsidiaries: string[];
  action: "nest" | "unnest";
};

export interface ActionItemsCounterparty {
  did: string;
  name: string;
  logoUrl: string;
}

export interface VaspActionItems {
  outgoingTransactionsCount: number;
  outgoingTransactionsMissingDataCount: number;
  incomingTransactionPendingResponseCount: number;
  pendingDDQCount: number;
  counterparties: ActionItemsCounterparty[];
}

export interface UserPreference {
  id: string;
  preference: string;
  isEnabled: boolean;
  vaspDID?: string;
}

export interface UserPreferenceInput {
  preference: string;
  is_enabled: boolean;
  vasp_did?: string;
}

export interface UserPreferenceResponse {
  id: string;
  user_did: string;
  preference: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
  vasp_did: string;
}
